import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminCoupons.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

type CouponType = "credits" | "payment_discount" | "payment_credits";
type DiscountType = "percentage" | "fixed_amount";
type OwnerMode = "admin" | "user" | "affiliate";

interface UserSearchResult {
  user_id: string;
  email: string;
  full_name: string;
}

interface AdminCoupon {
  id: string;
  code: string;
  affiliate_id?: string | null;
  owner_user_id?: string | null;
  coupon_type: CouponType;
  grants_credits: number;
  discount_type?: DiscountType | null;
  discount_value?: number | null;
  discount_value_sar?: number | null;
  is_active: boolean;
  is_public: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  max_uses_total?: number | null;
  max_uses_per_user?: number | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at?: string | null;
  redemption_count: number;
  window_status: "active" | "inactive" | "scheduled" | "expired" | "invalid";
  summary: string;
}

interface CouponFormState {
  id: string | null;
  code: string;
  couponType: CouponType;
  grantsCredits: string;
  discountType: DiscountType;
  discountValue: string;
  discountValueSar: string;
  isActive: boolean;
  isPublic: boolean;
  startsAt: string;
  endsAt: string;
  maxUsesTotal: string;
  maxUsesPerUser: string;
  ownerMode: OwnerMode;
  ownerUserId: string;
  affiliateId: string;
  metadata: string;
}

interface PromoFormState {
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
}

const defaultForm = (): CouponFormState => ({
  id: null,
  code: "",
  couponType: "payment_discount",
  grantsCredits: "5",
  discountType: "percentage",
  discountValue: "20",
  discountValueSar: "",
  isActive: true,
  isPublic: true,
  startsAt: "",
  endsAt: "",
  maxUsesTotal: "",
  maxUsesPerUser: "1",
  ownerMode: "admin",
  ownerUserId: "",
  affiliateId: "",
  metadata: "{}",
});

const toDatetimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const toApiDatetime = (value: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const formatDate = (value?: string | null) => {
  if (!value) return "No date";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const notify = (message: string, type: "success" | "error" | "warning" = "success") => {
  (window as any).showNotification?.(message, type);
};

const readApiError = (payload: any, fallback: string) =>
  payload?.error || payload?.message || fallback;

const AdminCoupons: React.FC = () => {
  const { user } = useAuthContext();
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [publicFilter, setPublicFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [form, setForm] = useState<CouponFormState>(defaultForm());
  const [ownerSearch, setOwnerSearch] = useState("");
  const [ownerResults, setOwnerResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [promoCoupon, setPromoCoupon] = useState<AdminCoupon | null>(null);
  const [promoForm, setPromoForm] = useState<PromoFormState>({
    titleEn: "",
    titleAr: "",
    bodyEn: "",
    bodyAr: "",
  });

  const fetchCoupons = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: user.id,
        status: statusFilter,
        type: typeFilter,
        public: publicFilter,
        query,
        page: String(page),
        per_page: "50",
      });
      const res = await authenticatedFetch(`${apiUrl}/api/admin/coupons?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to load coupons"));
      if (Array.isArray(data)) {
        // Backward compatibility while the backend deployment rolls out.
        setCoupons(data);
        setTotalCoupons(data.length);
        setTotalPages(1);
      } else {
        setCoupons(Array.isArray(data?.items) ? data.items : []);
        setTotalCoupons(Number.isFinite(data?.total) ? data.total : 0);
        setTotalPages(Number.isFinite(data?.pages) ? Math.max(1, data.pages) : 1);
      }
    } catch (error: any) {
      notify(error.message || "Failed to load coupons", "error");
    } finally {
      setLoading(false);
    }
  }, [user?.id, statusFilter, typeFilter, publicFilter, query, page]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    if (!user?.id) return;
    const search = ownerSearch.trim();
    if ((form.ownerMode !== "user" && form.ownerMode !== "affiliate") || search.length < 2) {
      setOwnerResults([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await authenticatedFetch(
          `${apiUrl}/api/admin/users/search?user_id=${user.id}&query=${encodeURIComponent(search)}&limit=10`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(readApiError(data, "Failed to search users"));
        setOwnerResults(Array.isArray(data) ? data : []);
      } catch {
        setOwnerResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [ownerSearch, form.ownerMode, user?.id]);

  const selectedCoupon = useMemo(
    () => coupons.find((coupon) => coupon.id === form.id) || null,
    [coupons, form.id]
  );

  const publicPromoWarning = (coupon: AdminCoupon) => {
    if (!coupon.is_public) return null;
    if (coupon.coupon_type !== "payment_discount") {
      return "Public coupons only appear on pricing when they are payment discounts.";
    }
    if (coupon.window_status !== "active") {
      return `This public promo is ${coupon.window_status} and will not currently appear to users.`;
    }
    return null;
  };

  const editCoupon = (coupon: AdminCoupon) => {
    const ownerMode: OwnerMode = coupon.affiliate_id
      ? "affiliate"
      : coupon.owner_user_id && coupon.owner_user_id !== user?.id
        ? "user"
        : "admin";
    setForm({
      id: coupon.id,
      code: coupon.code,
      couponType: coupon.coupon_type,
      grantsCredits: String(coupon.grants_credits || ""),
      discountType: coupon.discount_type || "percentage",
      discountValue:
        coupon.discount_type === "percentage" ? String(coupon.discount_value || "") : "",
      discountValueSar:
        coupon.discount_type === "fixed_amount"
          ? String(coupon.discount_value_sar ?? ((coupon.discount_value || 0) / 100))
          : "",
      isActive: coupon.is_active,
      isPublic: coupon.is_public,
      startsAt: toDatetimeInput(coupon.starts_at),
      endsAt: toDatetimeInput(coupon.ends_at),
      maxUsesTotal: coupon.max_uses_total ? String(coupon.max_uses_total) : "",
      maxUsesPerUser: coupon.max_uses_per_user ? String(coupon.max_uses_per_user) : "",
      ownerMode,
      ownerUserId: coupon.owner_user_id || "",
      affiliateId: coupon.affiliate_id || "",
      metadata: JSON.stringify(coupon.metadata || {}, null, 2),
    });
    setOwnerSearch("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildPayload = (overrides: Partial<CouponFormState> = {}) => {
    const snapshot = { ...form, ...overrides };
    return {
      admin_id: user?.id,
      code: snapshot.code,
      coupon_type: snapshot.couponType,
      grants_credits: snapshot.grantsCredits,
      discount_type: snapshot.couponType === "payment_discount" ? snapshot.discountType : null,
      discount_value:
        snapshot.couponType === "payment_discount" && snapshot.discountType === "percentage"
          ? snapshot.discountValue
          : null,
      discount_value_sar:
        snapshot.couponType === "payment_discount" && snapshot.discountType === "fixed_amount"
          ? snapshot.discountValueSar
          : null,
      is_active: snapshot.isActive,
      is_public: snapshot.isPublic,
      starts_at: toApiDatetime(snapshot.startsAt),
      ends_at: toApiDatetime(snapshot.endsAt),
      max_uses_total: snapshot.maxUsesTotal,
      max_uses_per_user: snapshot.maxUsesPerUser,
      metadata: snapshot.metadata,
      owner_mode: snapshot.ownerMode,
      owner_user_id: snapshot.ownerUserId,
      affiliate_id: snapshot.affiliateId,
    };
  };

  const saveCoupon = async (forceInactive = false) => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const payload = buildPayload(forceInactive ? { isActive: false } : {});
      const endpoint = form.id
        ? `${apiUrl}/api/admin/coupons/${form.id}`
        : `${apiUrl}/api/admin/coupons`;
      const method = form.id ? "PUT" : "POST";
      const res = await authenticatedFetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to save coupon"));

      await fetchCoupons();
      setForm(defaultForm());
      notify(form.id ? "Coupon updated" : "Coupon created", "success");

      if (data?.is_active && data?.is_public && data?.coupon_type === "payment_discount") {
        startPromoAnnouncement(data);
      }
    } catch (error: any) {
      notify(error.message || "Failed to save coupon", "error");
    } finally {
      setSaving(false);
    }
  };

  const deactivateCoupon = async (coupon: AdminCoupon) => {
    if (!user?.id) return;
    setActionLoading(coupon.id);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/admin/coupons/${coupon.id}/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ admin_id: user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to deactivate coupon"));
      await fetchCoupons();
      notify("Coupon deactivated", "success");
    } catch (error: any) {
      notify(error.message || "Failed to deactivate coupon", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const startPromoAnnouncement = (coupon: AdminCoupon) => {
    setPromoCoupon(coupon);
    const fixedValue = ((coupon.discount_value || 0) / 100).toFixed(2);
    const offer =
      coupon.discount_type === "percentage"
        ? `${coupon.discount_value}% off`
        : `${fixedValue} SAR off`;
    setPromoForm({
      titleEn: "Limited time offer",
      titleAr: "",
      bodyEn: `Use code ${coupon.code} for ${offer} at checkout.`,
      bodyAr: "",
    });
  };

  const createPromoAnnouncement = async () => {
    if (!user?.id || !promoCoupon) return;
    setActionLoading(`promo-${promoCoupon.id}`);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/admin/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          admin_id: user.id,
          status: "published",
          variant: "promo",
          priority: 10,
          start_at: promoCoupon.starts_at || null,
          end_at: promoCoupon.ends_at || null,
          is_dismissible: true,
          sticky: true,
          locations: ["global", "pricing"],
          title_translations: { en: promoForm.titleEn, ar: promoForm.titleAr },
          body_translations: { en: promoForm.bodyEn, ar: promoForm.bodyAr },
          cta: {
            text_translations: { en: "View plans", ar: "" },
            url: `/pricing?coupon=${encodeURIComponent(promoCoupon.code)}`,
            track: `coupon_${promoCoupon.code.toLowerCase()}_promo`,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Failed to create announcement"));
      notify("Promo announcement created", "success");
      setPromoCoupon(null);
    } catch (error: any) {
      notify(error.message || "Failed to create announcement", "error");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Coupons</h1>
          <p>Create payment discounts, credit coupons, and public promos.</p>
        </div>
        <button className={styles.secondaryButton} onClick={() => setForm(defaultForm())}>
          New Coupon
        </button>
      </div>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>{form.id ? `Edit ${selectedCoupon?.code || "coupon"}` : "Create coupon"}</h2>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Code</span>
            <input
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
              className={styles.input}
              placeholder="SUMMER20"
            />
          </label>

          <label className={styles.field}>
            <span>Coupon type</span>
            <select
              value={form.couponType}
              onChange={(event) =>
                setForm({ ...form, couponType: event.target.value as CouponType })
              }
              className={styles.select}
            >
              <option value="payment_discount">Payment discount</option>
              <option value="credits">Credits</option>
              <option value="payment_credits">Payment bonus credits</option>
            </select>
          </label>

          {form.couponType === "payment_discount" ? (
            <>
              <label className={styles.field}>
                <span>Discount type</span>
                <select
                  value={form.discountType}
                  onChange={(event) =>
                    setForm({ ...form, discountType: event.target.value as DiscountType })
                  }
                  className={styles.select}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed_amount">Fixed SAR amount</option>
                </select>
              </label>
              <label className={styles.field}>
                <span>{form.discountType === "percentage" ? "Discount %" : "Discount SAR"}</span>
                <input
                  value={form.discountType === "percentage" ? form.discountValue : form.discountValueSar}
                  onChange={(event) =>
                    form.discountType === "percentage"
                      ? setForm({ ...form, discountValue: event.target.value })
                      : setForm({ ...form, discountValueSar: event.target.value })
                  }
                  className={styles.input}
                  inputMode="decimal"
                />
              </label>
            </>
          ) : (
            <label className={styles.field}>
              <span>Credits granted</span>
              <input
                value={form.grantsCredits}
                onChange={(event) => setForm({ ...form, grantsCredits: event.target.value })}
                className={styles.input}
                inputMode="numeric"
              />
            </label>
          )}

          <label className={styles.field}>
            <span>Starts at</span>
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
              className={styles.input}
            />
          </label>

          <label className={styles.field}>
            <span>Ends at</span>
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
              className={styles.input}
            />
          </label>

          <label className={styles.field}>
            <span>Total uses</span>
            <input
              value={form.maxUsesTotal}
              onChange={(event) => setForm({ ...form, maxUsesTotal: event.target.value })}
              className={styles.input}
              placeholder="Unlimited"
              inputMode="numeric"
            />
          </label>

          <label className={styles.field}>
            <span>Uses per user</span>
            <input
              value={form.maxUsesPerUser}
              onChange={(event) => setForm({ ...form, maxUsesPerUser: event.target.value })}
              className={styles.input}
              placeholder="Unlimited"
              inputMode="numeric"
            />
          </label>

          <label className={styles.field}>
            <span>Owner</span>
            <select
              value={form.ownerMode}
              onChange={(event) =>
                setForm({
                  ...form,
                  ownerMode: event.target.value as OwnerMode,
                  ownerUserId: "",
                  affiliateId: "",
                })
              }
              className={styles.select}
            >
              <option value="admin">Creating admin</option>
              <option value="user">Existing user</option>
              <option value="affiliate">Affiliate</option>
            </select>
          </label>

          {(form.ownerMode === "user" || form.ownerMode === "affiliate") && (
            <div className={styles.field}>
              <span>{form.ownerMode === "affiliate" ? "Affiliate user ID" : "Owner user"}</span>
              <input
                value={form.ownerMode === "affiliate" ? form.affiliateId : form.ownerUserId}
                onChange={(event) =>
                  form.ownerMode === "affiliate"
                    ? setForm({ ...form, affiliateId: event.target.value })
                    : setForm({ ...form, ownerUserId: event.target.value })
                }
                className={styles.input}
                placeholder="Select below or paste UUID"
              />
              <input
                value={ownerSearch}
                onChange={(event) => setOwnerSearch(event.target.value)}
                className={`${styles.input} ${styles.searchInput}`}
                placeholder="Search users by email or name"
              />
              {searchLoading && <div className={styles.helpText}>Searching...</div>}
              {ownerResults.length > 0 && (
                <div className={styles.searchResults}>
                  {ownerResults.map((result) => (
                    <button
                      key={result.user_id}
                      type="button"
                      onClick={() => {
                        if (form.ownerMode === "affiliate") {
                          setForm({ ...form, affiliateId: result.user_id });
                        } else {
                          setForm({ ...form, ownerUserId: result.user_id });
                        }
                        setOwnerSearch(result.email);
                        setOwnerResults([]);
                      }}
                    >
                      <strong>{result.email}</strong>
                      <span>{result.full_name || result.user_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Active
          </label>

          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.isPublic}
              onChange={(event) => setForm({ ...form, isPublic: event.target.checked })}
            />
            Public on pricing
          </label>
        </div>

        <label className={styles.field}>
          <span>Metadata JSON</span>
          <textarea
            value={form.metadata}
            onChange={(event) => setForm({ ...form, metadata: event.target.value })}
            className={styles.textarea}
            rows={4}
          />
        </label>

        <div className={styles.actionsRow}>
          <button
            className={styles.primaryButton}
            onClick={() => saveCoupon(false)}
            disabled={saving}
          >
            {saving ? "Saving..." : form.id ? "Update coupon" : "Create coupon"}
          </button>
          <button
            className={styles.secondaryButton}
            onClick={() => saveCoupon(true)}
            disabled={saving}
          >
            Save as inactive
          </button>
        </div>
      </section>

      {promoCoupon && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2>Create promo announcement for {promoCoupon.code}</h2>
            <button className={styles.linkButton} onClick={() => setPromoCoupon(null)}>
              Dismiss
            </button>
          </div>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>English title</span>
              <input
                className={styles.input}
                value={promoForm.titleEn}
                onChange={(event) => setPromoForm({ ...promoForm, titleEn: event.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Arabic title</span>
              <input
                className={styles.input}
                value={promoForm.titleAr}
                onChange={(event) => setPromoForm({ ...promoForm, titleAr: event.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>English body</span>
              <input
                className={styles.input}
                value={promoForm.bodyEn}
                onChange={(event) => setPromoForm({ ...promoForm, bodyEn: event.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Arabic body</span>
              <input
                className={styles.input}
                value={promoForm.bodyAr}
                onChange={(event) => setPromoForm({ ...promoForm, bodyAr: event.target.value })}
              />
            </label>
          </div>
          <button
            className={styles.primaryButton}
            disabled={actionLoading === `promo-${promoCoupon.id}`}
            onClick={createPromoAnnouncement}
          >
            {actionLoading === `promo-${promoCoupon.id}` ? "Creating..." : "Create promo announcement"}
          </button>
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Coupon library</h2>
          <button className={styles.secondaryButton} onClick={fetchCoupons} disabled={loading}>
            Refresh
          </button>
        </div>

        <div className={styles.filters}>
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            className={styles.input}
            placeholder="Search code"
          />
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="expired">Expired</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={typeFilter}
            onChange={(event) => {
              setTypeFilter(event.target.value);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="all">All types</option>
            <option value="payment_discount">Payment discount</option>
            <option value="credits">Credits</option>
            <option value="payment_credits">Payment bonus credits</option>
          </select>
          <select
            value={publicFilter}
            onChange={(event) => {
              setPublicFilter(event.target.value);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="all">Public and private</option>
            <option value="public">Public only</option>
            <option value="private">Private only</option>
          </select>
        </div>

        {loading ? (
          <div className={styles.emptyState}>Loading coupons...</div>
        ) : coupons.length === 0 ? (
          <div className={styles.emptyState}>No coupons match these filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Benefit</th>
                  <th>Status</th>
                  <th>Visibility</th>
                  <th>Uses</th>
                  <th>Window</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => {
                  const warning = publicPromoWarning(coupon);
                  return (
                    <tr key={coupon.id}>
                      <td>
                        <strong>{coupon.code}</strong>
                        {warning && <div className={styles.warningText}>{warning}</div>}
                      </td>
                      <td>{coupon.summary}</td>
                      <td>
                        <span className={`${styles.badge} ${styles[coupon.window_status]}`}>
                          {coupon.window_status}
                        </span>
                      </td>
                      <td>{coupon.is_public ? "Public" : "Private"}</td>
                      <td>{coupon.redemption_count}</td>
                      <td>
                        <div>{formatDate(coupon.starts_at)}</div>
                        <div className={styles.helpText}>{formatDate(coupon.ends_at)}</div>
                      </td>
                      <td>
                        <div className={styles.tableActions}>
                          <button className={styles.linkButton} onClick={() => editCoupon(coupon)}>
                            Edit
                          </button>
                          {coupon.is_active && (
                            <button
                              className={styles.linkButtonDanger}
                              onClick={() => deactivateCoupon(coupon)}
                              disabled={actionLoading === coupon.id}
                            >
                              {actionLoading === coupon.id ? "..." : "Deactivate"}
                            </button>
                          )}
                          {coupon.is_active && coupon.is_public && coupon.coupon_type === "payment_discount" && (
                            <button className={styles.linkButton} onClick={() => startPromoAnnouncement(coupon)}>
                              Announce
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalCoupons > 0 && (
          <div className={styles.pagination}>
            <span>
              Page {page} of {totalPages} · {totalCoupons} coupon{totalCoupons === 1 ? "" : "s"}
            </span>
            <div className={styles.paginationActions}>
              <button
                className={styles.secondaryButton}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                className={styles.secondaryButton}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminCoupons;
